<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
/* eslint-disable vue/no-mutating-props */
// NOTE: This component intentionally mutates the companyRequest prop as part of its design.
// The parent component passes a buffer object that is expected to be modified in-place.
// We kept this approach when we refactored from vue-class-component to Composition API.
// @TODO: rework the entire state management to a more modern approach.
import { computed, onMounted, ref, shallowRef } from 'vue';
import i18n from '@/localisation/i18n';
import { isEmpty, isNull } from 'lodash';
import { TradingPermissionOption } from '@/utils/helpers/rest';
import Decimal from 'decimal.js';
import NumericInput from '@/modules/common/components/NumericInput.vue';
import { errorString, i18nServerMessage } from '@/utils/helpers/rest-response';
import { FormValidator, reValidNsccAccountNumber } from '@/utils/helpers/input-form';
import { formatTradingPermissionOption } from '@/modules/broker-admin/utils';
import CounterpartySearch from '@/modules/user-accounts/components/CounterpartySearch.vue';
import MultipleCounterpartySearch from '@/modules/user-accounts/components/MultipleCounterpartySearch.vue';
import { SettlementType, settlementTypes } from '@/connect';
import { isValidEmailAddress } from '@/utils/helpers/helpers';
import { CompanyRequest, DeliverOrderSender, NsccAccount } from '@/modules/common/models/admin';
import { ApiError } from '@/utils/errors/api-error';
import axios, { AxiosError } from 'axios';
import logger from '@/modules/common/services/logger.service';
import { useAgreementsStore } from '@/store/agreements-store';
import { store as vuex } from '@/store/store';

const props = defineProps<{
  selectedCompanyId: string | null;
  companyRequest: CompanyRequest;
}>();

const emit = defineEmits<{
  (event: 'close-modal'): void;
}>();

const agreementsStore = useAgreementsStore();

const accountNumberRules = [
  (value: string): true | string => !!value || 'Account ID is required.',
  (value: string): true | string => {
    return reValidNsccAccountNumber.test(value) || 'Invalid NSCC account number';
  },
];

const displayIdRules = [
  (value: string): true | string => !!value || 'Company Display Id is required.',
  (value: string): true | string => {
    return value.length === 4 || 'Company Display Id must be of length 4';
  },
];

const dtcParticipantIdRules = [
  (value: string): true | string => !!value || 'DTC Participant Id is required.',
  (value: string): true | string => {
    return value.length === 4 || 'DTC Participant Id must be of length 4';
  },
];

const emailListRule = [
  // should be comma separate list of emails OR empty
  (value: string[]): true | string => {
    if (!value || value.length === 0) {
      return true;
    }

    const invalidItems = value.filter((s) => s && !isValidEmailAddress(s));

    if (invalidItems.length > 0) {
      return `Invalid email address(es) ${invalidItems.join(', ')}`;
    }

    return true;
  },
];

const formRef = ref<{ validate: () => boolean } | null>(null);
const investorProfiles = shallowRef<Array<{ id: string; label: string }>>([]);
const processing = ref(false);
const tradingPermissionOptions = shallowRef<TradingPermissionOption[]>([]);
const deliverOrderSenderOptions = [
  { id: 'provable' as DeliverOrderSender, label: i18n.tc('deliverOrderSender.options.provable') },
  { id: 'loanet' as DeliverOrderSender, label: i18n.tc('deliverOrderSender.options.loanet') },
  { id: 'self' as DeliverOrderSender, label: i18n.tc('deliverOrderSender.options.self') },
];
const clErrors = ref<Record<string, string>>({});
const svErrors = ref('');
const validationError = ref('');
const tabIndex = ref(0);

const defaultOrderSoftLimit = shallowRef<Decimal | null>(null);
const defaultOrderHardLimit = shallowRef<Decimal | null>(null);
const softLimitTouched = ref(false);
const hardLimitTouched = ref(false);

// Soft limit validation - required, >= 1
const softLimitErrors = computed<string[]>(() => {
  if (softLimitTouched.value) {
    if (isNull(defaultOrderSoftLimit.value)) {
      return ['Soft limit is required'];
    }
    if (defaultOrderSoftLimit.value.lessThan(1)) {
      return ['Soft limit must be at least $1'];
    }
  }
  return [];
});

// Hard limit validation - required, >= 1, > soft limit
const hardLimitErrors = computed<string[]>(() => {
  const errors: string[] = [];

  if (hardLimitTouched.value) {
    if (isNull(defaultOrderHardLimit.value)) {
      errors.push('Hard limit is required');
    } else {
      if (defaultOrderHardLimit.value.lessThan(1)) {
        errors.push('Hard limit must be at least $1');
      }
      if (
        !isNull(defaultOrderSoftLimit.value) &&
        defaultOrderHardLimit.value.lessThanOrEqualTo(defaultOrderSoftLimit.value)
      ) {
        errors.push('Hard limit must exceed soft limit');
      }
    }
  }

  return errors;
});

function touchSoftLimit(): void {
  softLimitTouched.value = true;
}

function touchHardLimit(): void {
  hardLimitTouched.value = true;
}

function validateLimits(): boolean {
  touchSoftLimit();
  touchHardLimit();

  return softLimitErrors.value.length === 0 && hardLimitErrors.value.length === 0;
}

const settlementTypeOptions = computed((): Array<{ id: string; name: string }> => {
  return [...settlementTypes.entries()].map(([id, name]) => ({ id: SettlementType[id], name }));
});

const allowedPermissions = computed((): TradingPermissionOption[] => {
  if (props.companyRequest.nsccAccounts.length > 0) {
    return tradingPermissionOptions.value;
  } else {
    return tradingPermissionOptions.value.filter((o) => o.permission === 0);
  }
});

const clientConfig = computed(() => vuex.state.clientConfig);

const autoDisableInactiveDaysMin = computed(
  () => clientConfig.value?.autoDisableInactiveDaysMin ?? 90
);

const autoDisableInactiveEnabled = computed({
  get: () => props.companyRequest.autoDisableInactiveDays !== null,
  set: (enabled: boolean) => {
    if (enabled) {
      props.companyRequest.autoDisableInactiveDays = autoDisableInactiveDaysMin.value;
    } else {
      props.companyRequest.autoDisableInactiveDays = null;
    }
  },
});

const autoDisableInactiveDaysRules = computed(() => [
  (v: number | null): true | string =>
    v === null ||
    v >= autoDisableInactiveDaysMin.value ||
    `Must be at least ${autoDisableInactiveDaysMin.value} days`,
]);

// @TODO: we have this function duplicated in some components
// we should move to a service when we start fetching with connect-go
async function fetchTradingPermissionOptions(): Promise<void> {
  try {
    const { data } = await axios.get<{ options: TradingPermissionOption[] }>(
      '/api/1/trader/trading-permission-options'
    );
    tradingPermissionOptions.value = data.options;
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function createBrokerCompany(companyRequest: CompanyRequest): Promise<void> {
  try {
    await axios.post('/api/1/broker-admin/companies', CompanyRequest.toData(companyRequest));
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function updateBrokerCompany(payload: {
  companyRequest: CompanyRequest;
  companyId: string;
}): Promise<void> {
  try {
    await axios.put(
      `/api/1/broker-admin/companies/${payload.companyId}`,
      CompanyRequest.toData(payload.companyRequest)
    );
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function fetchAdminInvestorProfiles(): Promise<void> {
  try {
    const { data } = await axios.get<{ profiles: Array<{ id: string; label: string }> }>(
      '/api/1/broker-admin/investor-profiles'
    );
    investorProfiles.value = data.profiles;
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

// NSCC Account Management
function onAddNsccAccount(): void {
  let lastKey =
    props.companyRequest.nsccAccounts[props.companyRequest.nsccAccounts.length - 1]?.key ?? 0;
  const newAccount = NsccAccount.fromData({
    mainID: '',
    subID: '',
    isActive: props.companyRequest.nsccAccounts.length === 0,
  });
  newAccount.key = ++lastKey;
  props.companyRequest.nsccAccounts.push(newAccount);
}

function onDeleteNsccAccount(account: NsccAccount): void {
  const isDeletedActive = account.isActive;
  props.companyRequest.nsccAccounts = props.companyRequest.nsccAccounts.filter(
    (item) => item !== account
  );

  // activate the first account when the former active account was just deleted
  if (isDeletedActive && props.companyRequest.nsccAccounts.length) {
    props.companyRequest.nsccAccounts[0].isActive = true;
  }
}

function onActivateNsccAccount(account: NsccAccount): void {
  props.companyRequest.nsccAccounts.forEach((row) => {
    row.isActive = row.mainID === account.mainID;
  });
}

async function onSubmit(): Promise<void> {
  if (processing.value) {
    return;
  }
  processing.value = true;

  try {
    svErrors.value = '';
    validationError.value = '';

    // Validate General tab fields
    clErrors.value = new FormValidator(props.companyRequest, [
      'name',
      'profileID',
      'deliverOrderSender',
    ]).check();

    const canSubmitCompany = isEmpty(clErrors.value);
    const canSubmitNsccAccounts = props.companyRequest.nsccAccounts.every((row) => {
      const errors = new FormValidator(row, ['mainID', 'subID']).check();

      row.errorMainID = errors.mainID || '';
      row.errorSubID = errors.subID || '';

      return !errors.mainID && !errors.subID;
    });
    const canSubmitComplianceEmails = props.companyRequest.complianceEmailAddresses.every(
      (s) => !s || isValidEmailAddress(s)
    );
    const canSubmitForm = formRef.value?.validate() ?? true;

    const hasGeneralTabErrors =
      !canSubmitCompany || !canSubmitNsccAccounts || !canSubmitComplianceEmails || !canSubmitForm;

    // Validate Risk Limits tab
    const canSubmitLimits = validateLimits();
    const hasLimitsTabErrors = !canSubmitLimits;

    // Handle validation errors - prioritize General tab
    if (hasGeneralTabErrors || hasLimitsTabErrors) {
      validationError.value = 'Please fix the errors above.';

      if (hasGeneralTabErrors) {
        tabIndex.value = 0;
      } else if (hasLimitsTabErrors) {
        tabIndex.value = 1;
      }
      return;
    }

    // Sync limit values with companyRequest before API call
    props.companyRequest.defaultOrderSoftLimit = defaultOrderSoftLimit.value;
    props.companyRequest.defaultOrderHardLimit = defaultOrderHardLimit.value;

    if (props.selectedCompanyId === null) {
      await createBrokerCompany(props.companyRequest);
    } else {
      await updateBrokerCompany({
        companyRequest: props.companyRequest,
        companyId: props.selectedCompanyId,
      });
    }
    closeModalDialog();
  } catch (e) {
    svErrors.value = errorString(e as Error);
  } finally {
    processing.value = false;
  }
}

function closeModalDialog(): void {
  // allow parent to cleanup
  emit('close-modal');
}

/**
 * Get a CSS class according to the provided loan status
 */
function getStatusClass(status: boolean): string {
  return status ? 'status-active' : 'status-inactive';
}

function formatTradingPermissionsLabel(option: TradingPermissionOption): string {
  return i18n.tc(formatTradingPermissionOption(option));
}

onMounted(async () => {
  try {
    await fetchAdminInvestorProfiles();
    await fetchTradingPermissionOptions();
    if (props.selectedCompanyId) {
      await agreementsStore.loadAgreements(props.selectedCompanyId);
    }
    // Initialize limit values from companyRequest
    defaultOrderSoftLimit.value = props.companyRequest.defaultOrderSoftLimit;
    defaultOrderHardLimit.value = props.companyRequest.defaultOrderHardLimit;
  } catch (e) {
    logger.warn(e);
  }
});
</script>

<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="800px"
    no-click-animation
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    scrollable
    :value="true"
  >
    <VForm ref="formRef" novalidate @submit.prevent>
      <VCard>
        <VCardTitle>
          <span class="headline">{{
            selectedCompanyId === null ? 'Add Company' : 'Change Company'
          }}</span>
        </VCardTitle>
        <VCardText>
          <VTabs v-model="tabIndex">
            <VTab>General</VTab>
            <VTab>Risk Limits</VTab>
          </VTabs>

          <VTabsItems v-model="tabIndex">
            <VTabItem class="company-tab pa-4">
              <VContainer>
                <VRow>
                  <VCol cols="6">
                    <VTextField
                      v-model="companyRequest.name"
                      autocomplete="company"
                      autofocus
                      :error-messages="clErrors.name"
                      :label="$t('companyName')"
                      placeholder="e.g. Safe Trading Co."
                      required
                    />
                  </VCol>
                  <VCol cols="6">
                    <VSelect
                      ref="companies"
                      v-model="companyRequest.profileID"
                      :error-messages="clErrors.profileID"
                      item-text="label"
                      item-value="id"
                      :items="investorProfiles"
                      :label="$t('companyCategory')"
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol cols="12">
                    <VTextField
                      v-model="companyRequest.displayId"
                      autocomplete="company"
                      label="Company Display Id"
                      placeholder="e.g. MSFT"
                      required
                      :rules="displayIdRules"
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol cols="12">
                    <VTextField
                      v-model="companyRequest.dtcParticipantId"
                      autocomplete="off"
                      :error-messages="clErrors.dtcParticipantId"
                      label="DTC Participant Id"
                      required
                      :rules="dtcParticipantIdRules"
                    />
                  </VCol>
                </VRow>
                <VRow v-if="!!selectedCompanyId">
                  <VCol>
                    <h5 class="mb-1">Company OMS Default Agreements</h5>
                    <span v-if="companyRequest.defaultOmsAgreements.length === 0">
                      No defaults selected
                    </span>
                    <div>
                      <VTooltip
                        v-for="agreement in companyRequest.defaultOmsAgreements"
                        :key="'defaultOmsAgreements' + agreement.id"
                        top
                      >
                        <template #activator="{ on, attrs }">
                          <VChip v-bind="attrs" class="mr-2" small v-on="on">
                            {{ agreementsStore.getAgreement(agreement.id)?.shortName ?? 'N/A' }}
                          </VChip>
                        </template>
                        <span>{{ agreementsStore.getAgreementDetails(agreement.id) }}</span>
                      </VTooltip>
                    </div>
                  </VCol>
                </VRow>
                <VRow v-if="!!selectedCompanyId && companyRequest.smartloanEnabled">
                  <VCol>
                    <h5 class="mb-1">Smartloan Agreements</h5>
                    <span v-if="companyRequest.smartloanAgreements.length === 0">
                      No agreements selected
                    </span>
                    <div>
                      <VTooltip
                        v-for="agreement in companyRequest.smartloanAgreements"
                        :key="'smartloanAgreements' + agreement.id"
                        top
                      >
                        <template #activator="{ on, attrs }">
                          <VChip v-bind="attrs" class="mr-2" small v-on="on">
                            {{ agreementsStore.getAgreement(agreement.id)?.shortName ?? 'N/A' }}
                          </VChip>
                        </template>
                        <span>{{ agreementsStore.getAgreementDetails(agreement.id) }}</span>
                      </VTooltip>
                    </div>
                  </VCol>
                </VRow>
                <VRow>
                  <VCol>
                    <VSheet class="elevation-10">
                      <VToolbar flat>
                        <VToolbarTitle>NSCC Accounts</VToolbarTitle>
                        <VSpacer />
                        <VBtn color="secondary" small @click="onAddNsccAccount">
                          <VIcon left>mdi-plus</VIcon>
                          Add
                        </VBtn>
                      </VToolbar>
                      <VContainer class="aurora-data-table">
                        <VRow class="header">
                          <VCol>Main Account ID</VCol>
                          <VCol>Sub Account ID</VCol>
                          <VCol>Status</VCol>
                          <VCol cols="1">&nbsp;</VCol>
                        </VRow>
                        <VRow v-for="account in companyRequest.nsccAccounts" :key="account.key">
                          <VCol>
                            <VTextField
                              v-model="account.mainID"
                              autofocus
                              class="nscc-account-number"
                              dense
                              :error-messages="account.errorMainID"
                              :rules="accountNumberRules"
                              single-line
                            />
                          </VCol>
                          <VCol>
                            <VTextField
                              v-model="account.subID"
                              class="nscc-account-number"
                              dense
                              :error-messages="account.errorSubID"
                              :rules="accountNumberRules"
                              single-line
                            />
                          </VCol>
                          <VCol>
                            <VChip
                              :class="getStatusClass(account.isActive)"
                              small
                              @click="onActivateNsccAccount(account)"
                            >
                              {{ account.isActive ? 'Active' : 'Inactive' }}
                            </VChip>
                          </VCol>
                          <VCol cols="1">
                            <VIcon small @click="onDeleteNsccAccount(account)">mdi-delete</VIcon>
                          </VCol>
                        </VRow>
                        <VRow>
                          <VCol cols="12">
                            <VAlert icon="mdi-information">
                              <span class="text--secondary">
                                You can add multiple accounts but only one can be active.
                                <br />Click the status to activate another account.
                              </span>
                            </VAlert>
                          </VCol>
                        </VRow>
                      </VContainer>
                    </VSheet>
                  </VCol>
                </VRow>

                <VRow v-if="companyRequest.nsccAccounts.length === 0">
                  <VCol cols="12">
                    <VAlert border="left" icon="mdi-information" type="info">
                      <span class="text--secondary">
                        The company has no permission to trade until you configure an NSCC account.
                      </span>
                    </VAlert>
                  </VCol>
                </VRow>
                <VRow>
                  <VCol>
                    <VSelect
                      ref="tradingPermissions"
                      v-model="companyRequest.tradingPermissions"
                      :disabled="companyRequest.nsccAccounts.length === 0"
                      :error-messages="clErrors.tradingPermissions"
                      :item-text="formatTradingPermissionsLabel"
                      item-value="permission"
                      :items="allowedPermissions"
                      :label="$t(`tradingPermissions.title`)"
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol cols="4">
                    <VCheckbox
                      v-model="companyRequest.hasLoanetIntegration"
                      :label="$t('companyLoanet.dialog')"
                    />
                  </VCol>
                  <VCol cols="4">
                    <VCheckbox
                      v-model="companyRequest.smartloanEnabled"
                      label="Smartloan Enabled"
                    />
                  </VCol>
                  <VCol cols="4">
                    <VSelect
                      v-model="companyRequest.deliverOrderSender"
                      :disabled="companyRequest.nsccAccounts.length === 0"
                      :error-messages="clErrors.deliverOrderSender"
                      item-text="label"
                      item-value="id"
                      :items="deliverOrderSenderOptions"
                      :label="$t('deliverOrderSender')"
                    />
                  </VCol>
                  <VCol cols="4">
                    <CounterpartySearch
                      v-model="companyRequest.sponsorCompany"
                      clearable
                      :exclude-company-id="selectedCompanyId ?? undefined"
                      label="Sponsored by"
                      placeholder="Sponsored by"
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol>
                    <VSelect
                      v-model="companyRequest.enabledSettlementTypes"
                      item-text="name"
                      item-value="id"
                      :items="settlementTypeOptions"
                      label="Enabled Marketplace Settlement Types"
                      multiple
                      placeholder="Select enabled settlement type"
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol>
                    <MultipleCounterpartySearch
                      :counterparties.sync="companyRequest.bilateralCounterparties"
                      :exclude-company-id="selectedCompanyId ?? undefined"
                      label="Bilateral Trade Management Counterparties"
                      :max-height="'10vh'"
                      mode="table"
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol>
                    <VCombobox
                      v-model="companyRequest.complianceEmailAddresses"
                      chips
                      clearable
                      deletable-chips
                      :delimiters="[',']"
                      :hint="$t('companyComplianceInbox.tip')"
                      :label="$t('companyComplianceInbox.label')"
                      multiple
                      persistent-hint
                      :rules="emailListRule"
                      single-line
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol cols="12">
                    <VTextField
                      v-model="companyRequest.smartLoanIntegrationUrl"
                      :error-messages="clErrors.smartloanUrl"
                      label="Smartloan url"
                      placeholder="e.g. https://example.com/"
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol cols="6">
                    <VTextField
                      v-model="companyRequest.smartLoanIntegrationUsername"
                      :error-messages="clErrors.smartloanUsername"
                      :label="$t('companySmartloanUsername')"
                    />
                  </VCol>
                  <VCol cols="6">
                    <VTextField
                      v-model="companyRequest.smartLoanIntegrationPassword"
                      :error-messages="clErrors.smartloanPassword"
                      :items="investorProfiles"
                      :label="$t('companySmartloanPassword')"
                      type="password"
                    />
                  </VCol>
                </VRow>
                <VRow>
                  <VCol cols="12">
                    <h4 class="mb-2">User Security</h4>
                  </VCol>
                </VRow>
                <VRow align="center">
                  <VCol cols="5">
                    <span>Auto-disable Inactive Users</span>
                    <br />
                    <small class="text--secondary">
                      Automatically disable users who haven't logged in for a specified number of
                      days.
                    </small>
                  </VCol>
                  <VCol cols="3">
                    <VSwitch v-model="autoDisableInactiveEnabled" hide-details label="Enable" />
                  </VCol>
                  <VCol cols="4">
                    <VTextField
                      v-if="autoDisableInactiveEnabled"
                      v-model.number="companyRequest.autoDisableInactiveDays"
                      dense
                      :min="autoDisableInactiveDaysMin"
                      :rules="autoDisableInactiveDaysRules"
                      suffix="days"
                      type="number"
                    />
                  </VCol>
                </VRow>
              </VContainer>
            </VTabItem>
            <VTabItem class="company-tab pa-4">
              <VContainer>
                <VRow dense>
                  <VCol class="pt-6 pb-0 subtitle-1">Per Order Soft Limit</VCol>
                </VRow>
                <VRow dense>
                  <VCol>
                    Fat-finger warning will trigger when users attempt to enter an order exceeding
                    this notional value.
                  </VCol>
                </VRow>
                <VRow align="center" dense>
                  <VCol>Company Default Soft Limit:</VCol>
                  <VCol>
                    <NumericInput
                      v-model="defaultOrderSoftLimit"
                      :error-messages="softLimitErrors"
                      :min="1"
                      :precision="0"
                      prefix="$"
                      type="decimal"
                      @blur="touchSoftLimit"
                      @input="touchSoftLimit"
                    />
                  </VCol>
                </VRow>
              </VContainer>
              <VContainer>
                <VRow dense>
                  <VCol class="pt-12 pb-0 subtitle-1">Per Order Hard Limit</VCol>
                </VRow>
                <VRow dense>
                  <VCol>
                    Hard limit will be enforced when users attempt to enter an order exceeding this
                    notional value, preventing order entry.
                  </VCol>
                </VRow>
                <VRow align="center" dense>
                  <VCol>Company Default Hard Limit:</VCol>
                  <VCol>
                    <NumericInput
                      v-model="defaultOrderHardLimit"
                      :error-messages="hardLimitErrors"
                      :min="1"
                      :precision="0"
                      prefix="$"
                      type="decimal"
                      @blur="touchHardLimit"
                      @input="touchHardLimit"
                    />
                  </VCol>
                </VRow>
              </VContainer>
            </VTabItem>
          </VTabsItems>
          <VContainer v-if="svErrors">
            <VAlert dense type="error">{{ svErrors }}</VAlert>
          </VContainer>
        </VCardText>
        <VCardActions class="pt-4">
          <VBtn class="d-none d-sm-flex" color="secondary" text @click="closeModalDialog">
            {{ $t('cancelButton') }}
          </VBtn>
          <VSpacer />
          <span v-if="validationError" class="error--text text-body-2 mr-4">
            {{ validationError }}
          </span>
          <VBtn color="primary" min-width="120" type="submit" @click="onSubmit">
            {{ $t('saveBrokerCompany') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>

<style lang="scss" scoped>
::v-deep {
  .inline-label {
    font-weight: bold;
  }

  .is-radiogroup {
    padding: 8px 0 0 16px;
  }

  .nscc-table table tr td {
    border: none !important;
  }

  .nscc-account-number input {
    text-transform: uppercase;
  }

  // @TODO use sass variables to store these colours
  .v-chip {
    &.status-active {
      color: #fff;
      background: #ffae00;
      width: 70px;
    }

    &.status-inactive {
      color: #fff;
      width: 70px;
    }
  }
}
</style>
